import {
  CheckCheck,
  CircleHelp,
  Clock,
  FileQuestion,
  Loader2,
  PlugZap,
  Sparkles,
  Split,
  type LucideIcon,
} from 'lucide-react';

import {
  DocReviewReason,
  type DocVerificationState,
  type RegistryIdentificationFilter,
} from '../types/documentCapture.types';

export type { DocVerificationState };

/**
 * Verification — the THIRD status axis (86d3x48q7, T13).
 *
 * The module already has two independent axes: document status (`DocScanStatus`) and link status
 * (`DocLinkStatus`). This is a third and must never be folded into either — a scan can be decoded
 * but unverified, or verified but unlinked. Merging them would make one chip mean two things and
 * lose the distinction the calibration window exists to measure.
 *
 * ## ⚠️ Naming constraint — read this before touching a label
 *
 * **"Verified" appears nowhere, and no label may imply the AI is authoritative.** The AI is one of
 * two fallible readers, and during the window it is being *measured*, not trusted. A label claiming
 * certainty makes people stop checking — which destroys the very sample the window collects. This
 * is why the agreement state is `Confirmed` (two readings matched) and not `Verified` (something
 * was guaranteed), and why the tooltip says so out loud.
 *
 * ## Plain English (86d3yeh4z)
 *
 * Owner, 2026-08-06: "use phrases in common english… don't use deep words such as 'corroborate'."
 * These labels are read by people clearing a queue, not by the people who built the pipeline, so the
 * words are the ordinary ones: no label or tooltip here says corroborate, contention or deferred
 * (the state KEYS keep their code names — those are not read by anyone clearing a queue).
 * Every state has exactly ONE name, shared by the chip, the registry filter and the calibration grid
 * — a state with two names is two states to whoever is reading.
 *
 * ## Who did the reading (owner, 2026-08-06)
 *
 * "Does the barcode itself have the ability to read?" — no. A barcode is a symbol that GETS read; the
 * barcode READER is the thing that reads it. So wherever a sentence here is about who did the
 * reading, the subject is the reader. "The barcode" survives only where the sentence is genuinely
 * about the symbol on the paper: whether one is there, where it sits, what it says.
 */
/** Semantic token family for a state. Never a raw colour — all three themes must work. */
export type VerificationTone = 'info' | 'success' | 'destructive' | 'warning' | 'muted';

export interface VerificationDisplay {
  label: string;
  tone: VerificationTone;
  icon: LucideIcon;
  /** Verbatim from bundle README §2. Renders in the chip tooltip. */
  tooltip: string;
  /** Spinners are suppressed under `prefers-reduced-motion` — see VerificationChip. */
  spins?: boolean;
}

export const VERIFICATION_DISPLAY: Record<DocVerificationState, VerificationDisplay> = {
  pending: {
    label: 'Checking',
    tone: 'info',
    icon: Loader2,
    spins: true,
    tooltip:
      'Queued for the second reader. The barcode reader’s result is already in — this is the independent check.',
  },
  deferred: {
    // Same words as the live-stream row for this state ("trying again"), on purpose: the feed and
    // the chip are two views of one scan, and two names for one state is two states to a reader.
    label: 'Trying again',
    tone: 'info',
    icon: Clock,
    tooltip:
      'The reader was busy, so the check will run again shortly. This is normal, it happens on its own, and nothing is lost.',
  },
  confirmed: {
    label: 'Confirmed',
    tone: 'success',
    icon: CheckCheck,
    tooltip:
      'Two independent readings — the barcode reader and the AI — returned the same number. That is agreement, not a guarantee.',
  },
  conflict: {
    label: 'Conflict',
    tone: 'destructive',
    icon: Split,
    tooltip: 'The barcode reader and the AI read different numbers. Neither is assumed right — a person decides.',
  },
  suggestion: {
    label: 'AI suggestion',
    tone: 'info',
    icon: Sparkles,
    tooltip:
      'The barcode reader couldn’t get a number; the AI read one off the page. It never links on its own — someone confirms it.',
  },
  unconfirmed: {
    // ⚠️ This name appears in THREE places — here, the registry filter below, and the calibration
    // grid's Stat tile in SecondReaderDialog. Changing one and not the others gives the state two
    // names. The stored filter VALUE stays 'unconfirmed'; only what people read changes.
    // "…reader", not "…read it": a barcode does not read anything, it gets read (owner, 2026-08-06).
    label: 'Only the barcode reader',
    tone: 'warning',
    icon: CircleHelp,
    tooltip:
      'The barcode reader read a number, but the AI couldn’t read one off the page, so there’s nothing to compare it with. Goes to review.',
  },
  unreadable: {
    label: 'Unreadable',
    tone: 'muted',
    icon: FileQuestion,
    tooltip: 'Neither reader got a number off this scan — the situation before any of this existed.',
  },
  unavailable: {
    // ⚠️ Deliberately NOT "Unavailable" — that reads as a synonym of "Unreadable" to someone
    // clearing a queue, and the two need different fixes. "Not checked" says the check never
    // happened; "Unreadable" says it happened and found nothing.
    label: 'Not checked',
    tone: 'destructive',
    icon: PlugZap,
    tooltip:
      'The AI call itself failed, so there was no second reading at all. A different problem from “the AI read nothing” — and a different fix.',
  },
};

/**
 * Which verification state a review item represents.
 *
 * Returns null for every reason raised outside the second reader — those items have no verification
 * story to tell, and inventing one ("not checked") for a pre-feature NetSuite item would be a lie
 * about history.
 */
export function verificationStateForReason(reason: DocReviewReason): DocVerificationState | null {
  switch (reason) {
    case DocReviewReason.IdentificationConflict:
      return 'conflict';
    case DocReviewReason.AiSuggestion:
      return 'suggestion';
    case DocReviewReason.Unconfirmed:
      return 'unconfirmed';
    case DocReviewReason.VerifierUnavailable:
      return 'unavailable';
    case DocReviewReason.AutoLinkAudit:
      // A spot check is an item raised ON an agreement — the readers agreed and it linked.
      return 'confirmed';
    default:
      return null;
  }
}

/**
 * Registry identification filter (bundle §4.4) — a FILTER, never a column.
 *
 * ⚠️ Not a column for three reasons, the first of which is a hard constraint: `registry-columns.tsx`
 * is deliberately static, with no visibility toggle, to stay clear of the React-Compiler/TanStack
 * staleness trap (CLAUDE.md `86d39bx22`). Beyond that, every registry row is already identified, so
 * a `Confirmed` chip on nearly every row is noise that trains people to ignore chips — and
 * verification describes how a number was ARRIVED AT (audit provenance), not what the document is.
 *
 * The filter row carries no such constraint, and "show me everything that got here on an AI
 * suggestion" is a real question after the window closes.
 */
export const REGISTRY_IDENTIFICATION_OPTIONS: { value: RegistryIdentificationFilter; label: string }[] = [
  { value: 'confirmed', label: 'Confirmed by two readers' },
  { value: 'suggestion', label: 'From an AI suggestion' },
  { value: 'conflict', label: 'Resolved from a conflict' },
  { value: 'unconfirmed', label: 'Only the barcode reader' },
  { value: 'unavailable', label: 'Not checked' },
  // ⚠️ Load-bearing option, not a tidy-up. Every document captured before this feature shipped has
  // NO attempts row at all, so without this bucket those rows answer no filter and look like they
  // were dropped. It is also the honest answer to "what predates the second reader?".
  { value: 'none', label: 'No second reading yet' },
];

export const REGISTRY_IDENTIFICATION_TOOLTIP =
  'How each number was arrived at. Most documents are Confirmed, so this is a lens for the exceptions — not something worth a permanent column.';

/**
 * Rows the registry shows per page before paging.
 *
 * ONE constant because the default is written in three places that must agree — the store's initial
 * `registryQuery`, the URL seed in `DocumentCapturePage`, and `RegistryPanel`'s own fallback. When
 * they drift, the first paint pages by one number and the refetch pages by another, and the footer
 * range lies for a frame.
 *
 * 10 rather than 25 (owner, 2026-08-04): the registry shares the page with the live stream and the
 * needs-review rail, so 25 rows pushed the pager itself below the fold — the control that exists to
 * prove the rest of the documents are reachable was the part nobody could see.
 */
export const DEFAULT_REGISTRY_PAGE_SIZE = 10;
